import {EventEmitter, Inject, Injectable} from '@angular/core';
import {ResponseDto} from '../dto/ResponseDto';
import {WebSocketClientRequestDto} from '../dto/WebSocketClientRequestDto';
import {MessageAttachment} from '../dto/MessageAttachment';
import {WebSocketEventDto} from '../dto/WebSocketEventDto';
import {BotatoWebSocketEvent} from '../dto/WebSocketEvent';
import {DialogEvent} from '../dto/DialogEvent';
import {Ng2DeviceService} from '../device-detector/ng2-device.service';
import {BotatoChatStorageService} from './botato-chat-storage.service';
import {CommunicationService} from './communication.service';
import {TranslateService} from '@ngx-translate/core';
import {BotatoConfig} from '../botato.config';
import {BotatoUserService} from './botato-user.service';
import {BotatoChatWebSocketDataInterceptor} from './botato-chat-web-socket-data-interceptor.service';
import {WebSocketMessage} from '../dto/WebSocketMessage';
import {io, Socket} from 'socket.io-client';
import {WebSocketAuthenticationRequestDto} from '../dto/WebSocketAuthenticationRequestDto';
import {
    InputSourceType
} from 'botato-botengine-browser/botato/botato-botengine/source/InstantMessagingAdapter/WebSocket/dto/InputSourceType.js';

@Injectable()
export class BotatoChatWebSocketCommunicationService extends CommunicationService {

    private connectingPromise: Promise<boolean> | undefined;

    public alreadyConnectChanged$: EventEmitter<boolean> = new EventEmitter<boolean>();
    public authenticationFailed: EventEmitter<void> = new EventEmitter<void>();
    public messageReceived: EventEmitter<ResponseDto> = new EventEmitter<ResponseDto>();
    public messageSent: EventEmitter<void> = new EventEmitter<void>();
    public eventSent: EventEmitter<void> = new EventEmitter<void>();
    public historyReceived: EventEmitter<(ResponseDto | string)[]> = new EventEmitter<(ResponseDto | string)[]>();
    public connectionStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    public startTyping: EventEmitter<ResponseDto> = new EventEmitter<ResponseDto>();
    public messageSendingStarted: EventEmitter<void> = new EventEmitter<void>();
    public messageSendingFinished: EventEmitter<void> = new EventEmitter<void>();
    public thinkingIndicator: EventEmitter<void> = new EventEmitter<void>();
    public unreadStatusReceived: EventEmitter<boolean> = new EventEmitter<boolean>();
    public lastReadAtReceived: EventEmitter<string | undefined> = new EventEmitter<string | undefined>();
    private accessTokenProvider: (() => Promise<string>) | undefined;
    private socket: Socket | undefined;
    public alreadyConnected: boolean = false;

    constructor(private deviceService: Ng2DeviceService,
                private storageService: BotatoChatStorageService,
                private translationService: TranslateService,
                private userService: BotatoUserService,
                private dataInterceptor: BotatoChatWebSocketDataInterceptor,
                @Inject('BOTATO_CONFIG') private botatoConfig: BotatoConfig) {
        super();
    }

    public async welcome(): Promise<void> {
        const accessToken = await this.getAccessToken();
        this.send(BotatoWebSocketEvent.EVENT, new WebSocketEventDto(DialogEvent.WELCOME, this.translationService.getCurrentLang(), this.deviceService.getDeviceInfo(), this.clientId, this.botId, undefined, undefined, accessToken));
        this.eventSent.emit();
        this.messageSendingStarted.emit();
    }

    public async sendMessage(text: string, inputSource: InputSourceType, attachment?: MessageAttachment): Promise<void> {
        const accessToken = await this.getAccessToken();
        const attachments = attachment !== undefined ? [attachment] : undefined;
        this.send(BotatoWebSocketEvent.MESSAGE, new WebSocketClientRequestDto(text, this.deviceService.getDeviceInfo(), this.translationService.getCurrentLang(), this.clientId, this.botId, inputSource, accessToken, attachments));
        this.messageSent.emit();
        this.messageSendingStarted.emit();
    }

    // eslint-disable-next-line
    public async sendEvent(eventName: string, inputSource: InputSourceType, eventPayload: any): Promise<void> {
        const accessToken = await this.getAccessToken();
        this.send(BotatoWebSocketEvent.EVENT, new WebSocketEventDto(eventName, this.translationService.getCurrentLang(), this.deviceService.getDeviceInfo(), this.clientId, this.botId, inputSource, eventPayload, accessToken));
        this.eventSent.emit();
        this.messageSendingStarted.emit();
    }

    public setAccessTokenProvider(provider: () => Promise<string>): void {
        this.accessTokenProvider = provider;
    }


    private send(type: string, data: WebSocketMessage): void {
        this.connect(false).then(async () => {
            await this.sendWithoutConnectionCheck(type, data);
        }).catch(error => console.log(error));
    }

    public async sendMarkAsRead(): Promise<void> {
        const accessToken = await this.getAccessToken();
        this.send(BotatoWebSocketEvent.MARK_AS_READ, new WebSocketEventDto('mark_as_read', this.translationService.getCurrentLang(), this.deviceService.getDeviceInfo(), this.clientId, this.botId, undefined, undefined, accessToken));
    }

    public setBotId(botId: string): void {
        this.botId = botId;
    }

    private async sendWithoutConnectionCheck(type: string, data: WebSocketMessage): Promise<void> {
        const userId = await this.userService.getUserId(this.botId);
        const botInUserId = userId.split('.');
        data.userId = (botInUserId.length === 1) ? userId : botInUserId[1];
        data = this.dataInterceptor.transform(type, data);
        if (this.socket === undefined) {
            throw new Error('Cannot send without a connected socket');
        }
        this.socket.emit(type, data);
    }

    public async connect(overtakeConnection: boolean): Promise<boolean> {
        if (this.connectingPromise !== undefined) {
            return this.connectingPromise;
        }

        if (this.socket === undefined || !this.socket.connected) {
            this.connectingPromise = this.setupSocket(overtakeConnection);
            const result = await this.connectingPromise;
            this.connectingPromise = undefined;
            return result;
        }

        return true;
    }

    public async disconnect(): Promise<void> {
        if (this.socket === undefined) {
            throw new Error('Cannot disconnect without a connected socket');
        }
        this.socket.disconnect();
        this.socket = undefined;

        return Promise.resolve();
    }

    private async setupSocket(overtakeConnection: boolean): Promise<boolean> {
        return new Promise((resolve) => {
            const socket = io(this.botatoConfig.socketUrl, {path: this.botatoConfig.socketPath});
            this.socket = socket;

            socket.on(BotatoWebSocketEvent.MESSAGE, (data: ResponseDto) => {
                this.lastBotResult = data;
                this.messageReceived.emit(data);
            });

            socket.on(DialogEvent.AUTHENTICATION_FAILED_FIRST_TRY, async (data: {
                type: string,
                data: { authToken: string | undefined }
            }) => {
                // As the first access token did not work, we get the latest one
                // this could be the case, when during the connect/authanticate the access token was refreshed
                const previousToken = data.data.authToken;
                try {
                    data.data.authToken = await this.waitForNewAccessToken(previousToken);
                } catch {
                    // No refreshed token showed up in time; treat it like a failed retry so the
                    // application can react (e.g. logout) instead of silently never authenticating.
                    this.authenticationFailed.emit();
                    return;
                }
                socket.emit(BotatoWebSocketEvent.RETRY_AFTER_AUTH_FAILED, {type: data.type, data: data.data});
            });

            socket.on(DialogEvent.AUTHENTICATION_FAILED_SECOND_TRY, () => this.authenticationFailed.emit());

            socket.on(BotatoWebSocketEvent.EVENT, (data: WebSocketEventDto) => {
                if (data.eventKey === DialogEvent.START_TYPING) {
                    this.startTyping.emit();
                    return;
                }

                if (data.eventKey === DialogEvent.MESSAGE_SENDING_STARTED) {
                    this.messageSendingStarted.emit();
                    return;
                }

                if (data.eventKey === DialogEvent.THINKING_INDICATOR) {
                    this.thinkingIndicator.emit();
                    return;
                }

                if (data.eventKey === DialogEvent.MESSAGE_SENDING_FINISHED) {
                    this.messageSendingFinished.emit();
                    return;
                }

                if (data.eventKey === DialogEvent.CONNECTED) {
                    this.alreadyConnected = false;
                    this.alreadyConnectChanged$.emit(false);
                    this.connectionStateChanged.emit(true);
                    resolve(true);
                    return;
                }

                if (data.eventKey === DialogEvent.ALREADY_CONNECTED_IN_OTHER_WINDOW) {
                    resolve(false);
                    this.alreadyConnected = true;
                    this.alreadyConnectChanged$.emit(true);
                    socket.disconnect();
                    return;
                }

                throw new Error(`Could not handle event '${data.eventKey}'`);
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            socket.on(BotatoWebSocketEvent.HISTORY, (data: any) => {
                if ((data as {messages?: unknown} | undefined)?.messages !== undefined) {
                    this.lastReadAtReceived.emit(data.lastReadAt as string | undefined);
                    this.historyReceived.emit(data.messages as (ResponseDto | string)[]);
                } else {
                    this.historyReceived.emit(data as (ResponseDto | string)[]);
                }
            });

            socket.on(BotatoWebSocketEvent.UNREAD_STATUS, (data: { hasUnread: boolean }) => {
                this.unreadStatusReceived.emit(data.hasUnread);
            });

            socket.on(BotatoWebSocketEvent.CONNECT, async () => {
                const accessToken = await this.getAccessToken();
                const authMessage = new WebSocketAuthenticationRequestDto(this.translationService.getCurrentLang(), this.deviceService.getDeviceInfo(), this.clientId, this.botId, overtakeConnection, accessToken);
                await this.sendWithoutConnectionCheck(BotatoWebSocketEvent.AUTHENTICATION, authMessage);
            });

            socket.on(BotatoWebSocketEvent.DISCONNECT, () => {
                this.messageSendingFinished.emit();
                this.connectionStateChanged.emit(false);
            });
        });
    }

    private async waitForNewAccessToken(previousToken: string | undefined): Promise<string | undefined> {
        const currentToken = await this.getAccessToken();
        if (previousToken !== currentToken) {
            return currentToken;
        }

        return new Promise((resolve, reject) => {
            let remainingRetries = 5;

            const interval = setInterval(async () => {
                const currentTokenFromRetry = await this.getAccessToken();
                if (previousToken !== currentTokenFromRetry) {
                    clearInterval(interval);
                    resolve(currentTokenFromRetry);
                    return;
                }

                remainingRetries--;

                if (remainingRetries === 0) {
                    clearInterval(interval);
                    reject(new Error('Could not retrieve access token'));
                    return;
                }

            }, 500);
        });
    }

    private async getAccessToken(): Promise<string | undefined> {
        if (this.accessTokenProvider === undefined) {
            return undefined;
        }
        const accessToken = await this.accessTokenProvider();
        if (accessToken === undefined) {
            throw Error('No access token provided by accessTokenProvider');
        }
        return accessToken;
    }
}
